import React, { useState, useEffect } from 'react';
import { testimonials } from '../data/mockData';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 5000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const getVisibleTestimonials = () => {
    const prevIndex = (currentIndex - 1 + testimonials.length) % testimonials.length;
    const nextIndex = (currentIndex + 1) % testimonials.length;
    return [
      { ...testimonials[prevIndex], position: 'left' },
      { ...testimonials[currentIndex], position: 'center' },
      { ...testimonials[nextIndex], position: 'right' }
    ];
  };

  const visibleTestimonials = getVisibleTestimonials();

  // Generate avatar image URL based on name
  const getAvatarUrl = (name) => {
    const seed = name.replace(/\s/g, '');
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`;
  };

  return (
    <section id="testimonials" className="relative py-20 bg-gradient-to-b from-gray-900 via-[#1a1a2e] to-gray-900 overflow-hidden">
      {/* Wave Divider Top */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none rotate-180">
        <svg className="relative block w-full h-20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-[#1a1a2e]"></path>
        </svg>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/3 left-10 w-96 h-96 bg-[#FF9900] rounded-full opacity-5 blur-3xl"></div>
      <div className="absolute bottom-1/3 right-10 w-96 h-96 bg-blue-500 rounded-full opacity-5 blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-gradient-to-r from-[#FF9900]/20 to-orange-600/20 border border-[#FF9900]/30 text-[#FF9900] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Client Success Stories
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-300">
            Real success stories from clients who protected their brands with our trademark registration services.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-8 py-8">
            {visibleTestimonials.map((testimonial, index) => {
              const isCenter = testimonial.position === 'center';
              const isLeft = testimonial.position === 'left';
              const isRight = testimonial.position === 'right';

              return (
                <Card
                  key={`${testimonial.id}-${index}`}
                  className={`
                    transition-all duration-500 ease-out
                    ${isCenter 
                      ? 'scale-100 opacity-100 z-20 w-full max-w-2xl' 
                      : 'scale-75 opacity-70 z-10 w-full max-w-xl hidden lg:block'
                    }
                    ${isLeft ? '-mr-32' : ''}
                    ${isRight ? '-ml-32' : ''}
                    ${isCenter 
                      ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm border-2 border-[#FF9900]' 
                      : 'bg-white border-2 border-gray-200'
                    }
                  `}
                >
                  <CardContent className="pt-8 pb-8 px-8">
                    {/* Quote Icon */}
                    <div className="flex items-center justify-between mb-6">
                      <Quote className={`w-12 h-12 ${isCenter ? 'text-[#FF9900]' : 'text-gray-400'} opacity-50`} />
                      <Badge className={`${isCenter ? 'bg-[#FF9900]/20 text-[#FF9900] border-[#FF9900]/30' : 'bg-gray-100 text-gray-700 border-gray-300'}`}>
                        {testimonial.service}
                      </Badge>
                    </div>

                    {/* Content */}
                    <p className={`${isCenter ? 'text-gray-200 text-lg' : 'text-gray-700 text-base'} leading-relaxed mb-6 min-h-[120px]`}>
                      "{testimonial.content}"
                    </p>

                    {/* Rating */}
                    <div className="flex items-center space-x-1 mb-6">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className={`w-5 h-5 ${isCenter ? 'fill-[#FF9900] text-[#FF9900]' : 'fill-gray-400 text-gray-400'}`} />
                      ))}
                    </div>

                    {/* Author Info - NO IMAGE */}
                    <div className="flex items-center space-x-4">
                      <div className={`${isCenter ? 'w-16 h-16' : 'w-12 h-12'} rounded-full flex items-center justify-center font-bold text-2xl ${isCenter ? 'bg-[#FF9900] text-black' : 'bg-gray-200 text-gray-600'}`}>
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <div className={`font-semibold ${isCenter ? 'text-white text-lg' : 'text-gray-800 text-base'}`}>
                          {testimonial.name}
                        </div>
                        <div className={`text-sm ${isCenter ? 'text-gray-400' : 'text-gray-600'}`}>
                          {testimonial.role}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              onClick={handlePrev}
              disabled={isAnimating}
              className="bg-gradient-to-r from-[#FF9900] to-orange-600 hover:from-[#FF9900]/90 hover:to-orange-600/90 text-black rounded-full w-12 h-12 p-0"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>

            {/* Dots Indicator */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (!isAnimating) {
                      setIsAnimating(true);
                      setCurrentIndex(index);
                      setTimeout(() => setIsAnimating(false), 500);
                    }
                  }}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentIndex
                      ? 'w-8 h-2 bg-gradient-to-r from-[#FF9900] to-orange-600'
                      : 'w-2 h-2 bg-gray-600 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>

            <Button
              onClick={handleNext}
              disabled={isAnimating}
              className="bg-gradient-to-r from-[#FF9900] to-orange-600 hover:from-[#FF9900]/90 hover:to-orange-600/90 text-black rounded-full w-12 h-12 p-0"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 max-w-4xl mx-auto bg-gradient-to-r from-[#FF9900]/20 to-orange-600/20 border border-[#FF9900]/30 rounded-3xl p-8">
          <h3 className="text-2xl font-bold text-white text-center mb-6">Trusted by Businesses Nationwide</h3>
          <p className="text-gray-300 text-center leading-relaxed mb-6">
            Our trademark registration services have helped thousands of businesses across the United States protect their brand identities and unlock powerful tools like Amazon Brand Registry. From startups to established enterprises, we provide the expertise and support needed for successful trademark registration.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-white font-bold mb-1">All Industries</div>
              <div className="text-gray-400 text-sm">Technology to Manufacturing</div>
            </div>
            <div className="text-center">
              <div className="text-white font-bold mb-1">Expert Support</div>
              <div className="text-gray-400 text-sm">Dedicated Case Managers</div>
            </div>
            <div className="text-center">
              <div className="text-white font-bold mb-1">Fast Processing</div>
              <div className="text-gray-400 text-sm">Efficient Filing Service</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;